import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Tooltip,
  Fab
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { useAuth } from '../context/AuthContext';

function LocalesAdmin() {
  const [locales, setLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (user && user.token) {
      fetchLocales();
    } else {
      setError('Usuario no autenticado');
      setLoading(false);
    }
  }, [user]);

  const fetchLocales = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/api/locales`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setLocales(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los locales:', error);
      setError('Error al cargar los locales. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar el local "${nombre}"?`)) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/locales/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchLocales();
      } catch (error) {
        console.error('Error al eliminar el local:', error);
        setError('Error al eliminar el local.');
      }
    }
  };

  const getInitials = (nombre) => {
    return nombre?.charAt(0)?.toUpperCase() || 'L';
  };

  // Vista móvil con tarjetas
  const LocalCard = ({ local }) => (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 3,
        border: '1px solid rgba(26, 35, 126, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              color: 'white',
              fontWeight: 'bold',
              mr: 2
            }}
          >
            {getInitials(local.nombre_local)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
              {local.nombre_local}
            </Typography>
            <Chip 
              label={`${local.num_puestos} puestos`} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(79, 195, 247, 0.1)',
                color: '#4FC3F7',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {local.direccion_local}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
            <Typography variant="body2" color="text.secondary">
              Capacidad: {local.num_puestos} puestos de trabajo
            </Typography>
          </Box>

          {local.fecha_sistema && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
              <Typography variant="caption" color="text.secondary">
                Creado: {new Date(local.fecha_sistema).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/home/locales/editar/${local.id_local}`)}
            sx={{ 
              bgcolor: 'primary.light', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.main' }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(local.id_local, local.nombre_local)}
            sx={{ 
              bgcolor: 'error.light', 
              color: 'white',
              '&:hover': { bgcolor: 'error.main' }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: '#1A237E',
              display: { xs: 'none', sm: 'flex' },
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>

          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              Gestión de Locales
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administra los locales de tu empresa
            </Typography>
          </Box>
        </Box>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/home/locales/crear')}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Crear Local
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Contenido */}
      {isMobile ? (
        /* Vista móvil - Cards */
        <Box>
          {locales.map((local) => (
            <LocalCard key={local.id_local} local={local} />
          ))}
        </Box>
      ) : (
        /* Vista desktop - Tabla */
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ 
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)' 
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Local
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Dirección
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Puestos
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locales.map((local, index) => (
                  <TableRow 
                    key={local.id_local}
                    sx={{ 
                      '&:nth-of-type(even)': { 
                        backgroundColor: 'rgba(26, 35, 126, 0.02)' 
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(79, 195, 247, 0.1)',
                        transform: 'scale(1.01)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {getInitials(local.nombre_local)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {local.nombre_local}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {local.id_local}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {local.direccion_local}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${local.num_puestos} puestos`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(79, 195, 247, 0.1)',
                          color: '#4FC3F7',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Editar local">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/home/locales/editar/${local.id_local}`)}
                            sx={{ 
                              bgcolor: 'primary.light', 
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.main' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar local">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(local.id_local, local.nombre_local)}
                            sx={{ 
                              bgcolor: 'error.light', 
                              color: 'white',
                              '&:hover': { bgcolor: 'error.main' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/locales/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
}

export default LocalesAdmin;